import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FaAndroid, FaApple, FaArrowUp, FaGooglePlay } from "react-icons/fa";

// Shared layout so both buttons match in size/shape; only the colors differ
const downloadButtonBase =
  "w-full py-3 font-bold flex items-center justify-center gap-2 rounded-xl shadow-md text-white transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed";
const androidButtonClass = `${downloadButtonBase} bg-primary-600 hover:bg-primary-700`;
const appStoreButtonClass = `${downloadButtonBase} bg-slate-900 hover:bg-slate-800`;
const playStoreButtonClass = `${downloadButtonBase} bg-emerald-600 hover:bg-emerald-700`;

interface ApkRelease {
  id: string;
  versionName: string;
  versionCode: number;
  downloadUrl: string;
}

// Store pages for the published builds (see apps/b2b-mobile store-link util)
const IOS_APP_STORE_URL = "https://apps.apple.com/app/id6756252845";
const ANDROID_PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.melody.pocket";

const getApiBaseUrl = () => {
  if (import.meta.env.VITE_API_BASE_URL) return import.meta.env.VITE_API_BASE_URL;
  const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4001/graphql";
  return apiUrl.replace(/\/graphql$/, "");
};

// Programmatically trigger a file download without navigating away from the page
const triggerDownload = (url: string) => {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

export function DownloadPage() {
  const [release, setRelease] = useState<ApkRelease | null>(null);
  const [loading, setLoading] = useState(true);
  // WeChat's in-app browser silently blocks direct .apk downloads, so we detect it
  // and guide users to reopen the page in their system browser instead.
  const [isWechat, setIsWechat] = useState(false);
  const [showWechatGuide, setShowWechatGuide] = useState(false);

  const fetchLatest = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${getApiBaseUrl()}/api/apk-releases`);

      if (!response.ok) {
        throw new Error("Failed to fetch APK releases");
      }

      const releases: ApkRelease[] = await response.json();
      if (releases.length === 0) {
        setRelease(null);
        return;
      }

      // Highest versionCode is the latest release
      setRelease(releases.reduce((a, b) => (b.versionCode > a.versionCode ? b : a)));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "載入最新版本失敗");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLatest();
  }, [fetchLatest]);

  // Detect the WeChat in-app browser on mount (client-only to avoid hydration mismatch)
  useEffect(() => {
    if (/micromessenger/i.test(navigator.userAgent)) {
      setIsWechat(true);
      setShowWechatGuide(true);
    }
  }, []);

  const handleAndroidDownload = () => {
    if (!release) {
      toast.error("目前沒有任何可下載的版本");
      return;
    }
    // The download would be blocked inside WeChat — surface the guide instead.
    if (isWechat) {
      setShowWechatGuide(true);
      return;
    }
    triggerDownload(release.downloadUrl);
    toast.success(`開始下載版本 ${release.versionName}`);
  };

  // ICP filing notice is only required on the mainland China (.cn) domain
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const isCnSite = host === "mypocketteacher.cn" || host.endsWith(".mypocketteacher.cn");

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-slate-900 transition-colors duration-300">
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md panel bg-white dark:bg-slate-800 shadow-md border border-slate-200/60 dark:border-slate-700/50 rounded-3xl p-8 flex flex-col items-center text-center space-y-6">
          {/* App icon — click to download the Android APK */}
          <img
            src="/icon/app_icon.png"
            alt="Pocket Teacher App"
            className="w-28 h-28 object-cover rounded-3xl"
          />

          {/* Title & current version */}
          <div className="space-y-1">
            <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100">
              Pocket Teacher App
            </h1>
            <p className="text-sm text-red-500 dark:text-red-400">
              目前版本：{loading ? "載入中..." : (release?.versionName ?? "—")}
            </p>
          </div>

          {/* Download buttons */}
          <div className="w-full space-y-3 pt-2">
            <button
              type="button"
              onClick={handleAndroidDownload}
              disabled={loading || !release}
              className={androidButtonClass}
            >
              <FaAndroid className="w-5 h-5" />
              下載 Android APK
            </button>

            <a
              href={ANDROID_PLAY_STORE_URL}
              target="_blank"
              rel="noreferrer"
              className={playStoreButtonClass}
            >
              <FaGooglePlay className="w-5 h-5" />
              前往 Google Play
            </a>

            <a
              href={IOS_APP_STORE_URL}
              target="_blank"
              rel="noreferrer"
              className={appStoreButtonClass}
            >
              <FaApple className="w-5 h-5" />
              前往 App Store
            </a>
          </div>
        </div>
      </div>

      {isCnSite && (
        <footer className="pb-6 px-6 text-center text-xs text-slate-500 dark:text-slate-400 space-y-1">
          <p>
            工信部网站:{" "}
            <a
              href="https://beian.miit.gov.cn"
              target="_blank"
              rel="noreferrer"
              className="text-blue-500 hover:underline"
            >
              beian.miit.gov.cn
            </a>
          </p>
          <p>
            ICP:{" "}
            <a
              href="https://beian.miit.gov.cn"
              target="_blank"
              rel="noreferrer"
              className="text-blue-500 hover:underline"
            >
              沪ICP备2025115330号-6
            </a>
          </p>
          <p>Copyright © 上海腾蒂教育科技有限公司 {new Date().getFullYear()}.</p>
        </footer>
      )}

      {/* WeChat guidance overlay — arrow points at the top-right "⋯" menu */}
      {showWechatGuide && (
        <button
          type="button"
          onClick={() => setShowWechatGuide(false)}
          className="fixed inset-0 z-50 flex flex-col p-6 text-white bg-slate-950/85 backdrop-blur-sm animate-fade-in"
        >
          <div className="flex pr-3 absolute right-0">
            <div className="flex flex-col items-center gap-1.5">
              <FaArrowUp className="w-12 h-12 text-amber-400 animate-bounce" />
              <span className="text-sm font-bold text-amber-300">點這裡</span>
            </div>
          </div>

          <div className="flex-1 flex flex-col items-center justify-center text-center space-y-4 max-w-xs mx-auto">
            <h2 className="text-2xl font-extrabold">請在瀏覽器中開啟</h2>
            <p className="text-sm leading-relaxed text-slate-200">
              微信內無法直接下載安裝檔。請點擊右上角的{" "}
              <span className="font-bold text-amber-300">「⋯」</span> 按鈕，選擇{" "}
              <span className="font-bold text-amber-300">「在瀏覽器中開啟」</span>，即可下載。
            </p>
            <span className="text-xs text-slate-400">（點擊任意處關閉提示）</span>
          </div>
        </button>
      )}
    </div>
  );
}
